import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { Dumbbell, Activity, Zap, Stethoscope, Check, X, Clock, Trash2 } from "lucide-react";
import { useEffectiveRole } from "@/lib/permissions";

import { BACKEND_URL as API } from "@/lib/api";
const HTTP = axios.create({ baseURL: `${API}/api`, withCredentials: true });

const TYPE_META = {
  strength:          { label: "Strength",      icon: Dumbbell,    color: "text-amber-300" },
  conditioning:      { label: "Conditioning",  icon: Activity,    color: "text-cyan-300" },
  speed_agility:     { label: "Speed/Agility", icon: Zap,         color: "text-violet-300" },
  athletic_training: { label: "AT Rehab",      icon: Stethoscope, color: "text-emerald-300" },
};

const ROLE_ALLOWED = {
  head_coach:       ["strength", "conditioning", "speed_agility", "athletic_training"],
  pitching_coach:   ["strength", "conditioning", "speed_agility", "athletic_training"],
  strength_coach:   ["strength", "conditioning", "speed_agility"],
  athletic_trainer: ["athletic_training"],
  assistant_coach:  [],
};

function TypeIcon({ type }) {
  const M = TYPE_META[type];
  if (!M) return null;
  const I = M.icon;
  return <I className={`h-4 w-4 ${M.color}`} />;
}

function dateFmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function CoachWorkload() {
  const { user } = useAuth();
  const { role } = useEffectiveRole();
  const allowed = ROLE_ALLOWED[role] || [];
  const canLog = allowed.length > 0;

  const [players, setPlayers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [pending, setPending] = useState([]);
  const [form, setForm] = useState({ player_user_id: "", session_type: allowed[0] || "strength", duration_min: 45, rpe: 7, notes: "" });
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [pl, ss, pen] = await Promise.all([
        HTTP.get("/players"),
        HTTP.get("/workload/sessions?days=14"),
        HTTP.get("/workload/pending"),
      ]);
      setPlayers(pl.data || []);
      setSessions(ss.data?.sessions || []);
      setPending(pen.data?.sessions || []);
    } catch (e) {
      toast.error("Could not load workload data");
    }
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!form.player_user_id && players[0]) setForm((f) => ({ ...f, player_user_id: players[0].user_id }));
    if (!allowed.includes(form.session_type) && allowed[0]) setForm((f) => ({ ...f, session_type: allowed[0] }));
  }, [players, role]);

  const submit = async () => {
    if (!form.player_user_id) return toast.error("Pick an athlete");
    setBusy(true);
    try {
      await HTTP.post("/workload/sessions", {
        player_user_id: form.player_user_id,
        session_type: form.session_type,
        duration_min: Number(form.duration_min) || 0,
        rpe: form.rpe ? Number(form.rpe) : null,
        notes: form.notes || null,
      });
      toast.success("Session logged");
      setForm((f) => ({ ...f, notes: "" }));
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log session");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (sid, decision) => {
    try {
      await HTTP.patch(`/workload/sessions/${sid}/verify`, { decision });
      toast.success(decision === "verified" ? "Verified ✓" : "Rejected");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (sid) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await HTTP.delete(`/workload/sessions/${sid}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const grouped = useMemo(() => {
    const m = {};
    for (const s of sessions) (m[s.player_user_id] = m[s.player_user_id] || []).push(s);
    return m;
  }, [sessions]);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="coach-workload-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">Workload &amp; Attendance</h1>
        <p className="text-white/60 mt-1 text-sm md:text-base">
          Log strength, conditioning, speed-agility, and athletic-training sessions. Verified work feeds the
          Readiness Score&apos;s load + recovery sub-scores.
        </p>
      </header>

      <Tabs defaultValue="log">
        <TabsList data-testid="workload-tabs">
          <TabsTrigger value="log" data-testid="tab-log">Log a Session</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending {pending.length ? <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] text-black">{pending.length}</span> : null}</TabsTrigger>
          <TabsTrigger value="recent" data-testid="tab-recent">Recent 14 days</TabsTrigger>
        </TabsList>

        <TabsContent value="log" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>New session</CardTitle>
              <CardDescription>
                {canLog ? `Your role can log: ${allowed.map((t) => TYPE_META[t].label).join(", ")}.` :
                  "Your current role can't log workload sessions. Ask a Head/Pitching Coach for permission."}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Athlete</Label>
                <Select value={form.player_user_id} onValueChange={(v) => setForm({ ...form, player_user_id: v })}>
                  <SelectTrigger data-testid="workload-player-select"><SelectValue placeholder="Choose..." /></SelectTrigger>
                  <SelectContent>
                    {players.map((p) => (
                      <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Session type</Label>
                <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
                  <SelectTrigger data-testid="workload-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowed.map((t) => (
                      <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" min={0} max={300} value={form.duration_min}
                  onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                  data-testid="workload-duration-input" />
              </div>
              <div>
                <Label>RPE (1–10){form.session_type === "athletic_training" ? " (optional)" : ""}</Label>
                <Input type="number" min={1} max={10} value={form.rpe ?? ""}
                  onChange={(e) => setForm({ ...form, rpe: e.target.value })}
                  data-testid="workload-rpe-input" />
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Lower-body lift, hex-bar deadlift PR…" data-testid="workload-notes-input" />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button onClick={submit} disabled={busy || !canLog} data-testid="workload-submit-btn">
                  Log session
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="mt-4 space-y-3">
          {pending.length === 0 && (
            <Card><CardContent className="py-8 text-center text-white/50">No pending self-logs to verify.</CardContent></Card>
          )}
          {pending.map((s) => (
            <Card key={s.session_id} data-testid={`pending-${s.session_id}`}>
              <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <TypeIcon type={s.session_type} />
                  <div>
                    <div className="font-medium">{s.player_name}</div>
                    <div className="text-xs text-white/50">
                      {TYPE_META[s.session_type]?.label} · {s.duration_min} min · {dateFmt(s.date)}
                    </div>
                    {s.notes && <div className="text-xs text-white/40 mt-1">&ldquo;{s.notes}&rdquo;</div>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => verify(s.session_id, "verified")} data-testid={`verify-${s.session_id}`}>
                    <Check className="h-4 w-4 mr-1" /> Verify
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => verify(s.session_id, "rejected")} data-testid={`reject-${s.session_id}`}>
                    <X className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="recent" className="mt-4 space-y-3">
          {Object.keys(grouped).length === 0 && (
            <Card><CardContent className="py-8 text-center text-white/50">No sessions logged in the last 14 days.</CardContent></Card>
          )}
          {Object.entries(grouped).map(([pid, list]) => {
            const p = players.find((x) => x.user_id === pid);
            return (
              <Card key={pid}>
                <CardHeader>
                  <CardTitle className="text-base">{p?.name || list[0]?.player_name || pid}</CardTitle>
                  <CardDescription>{list.length} session{list.length === 1 ? "" : "s"}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {list.map((s) => (
                    <div key={s.session_id} className="flex items-center justify-between rounded border border-white/10 p-2 text-sm">
                      <div className="flex items-center gap-2">
                        <TypeIcon type={s.session_type} />
                        <span className="font-medium">{TYPE_META[s.session_type]?.label}</span>
                        <span className="text-white/50">· {s.duration_min} min{s.rpe ? ` · RPE ${s.rpe}` : ""}</span>
                        <Badge variant={s.status === "verified" ? "default" : s.status === "rejected" ? "destructive" : "outline"}>
                          {s.status === "pending" ? <Clock className="h-3 w-3 mr-1" /> : null}{s.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-white/40">
                        <span>{dateFmt(s.date)}</span>
                        <button onClick={() => del(s.session_id)} title="Delete" data-testid={`del-${s.session_id}`}>
                          <Trash2 className="h-3.5 w-3.5 hover:text-rose-300" />
                        </button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
